import { useEffect, useContext, useState } from "react";
import { Button, LoadingSkeleton, Row, Pagination } from "@narmi/design_system";
import NotificationContext from "cerulean/src/NotificationContext";
import { useInstitutionContext } from "@hooks/institutionContext";

import styles from "./table.module.css";
import { AdminApiSchema } from "../../api_utils";
import { NoResultsFound } from "../NoResultsFound";
import { FEDNOW_TRANSACTION_STATES } from "./codesets";
import FedNowTransactionDrawer from "./FedNowTransactionDrawer";
import { moneyFormatter } from "./PaymentTable";
import SearchBar from "./SearchBar";
import { getEvaluationDescription } from "@utils/evaluations";

type FedNowTransaction = AdminApiSchema.components["schemas"]["FedNowTransactionAdmin"];
const PAGE_SIZE = 100;
export type TextQuery = { text: string | undefined };

// Using the FedNowTransaction model from banking,
// unlike PaymentTable which the source is Payment model in fednow.
export default function FedNowTransactionTable({
  state,
  selectedId,
}: {
  state: string;
  selectedId: FedNowTransaction["id"] | undefined;
}) {
  const { sendNotification } = useContext(NotificationContext);
  const [page, setPage] = useState(1);
  const [transactions, setTransactions] = useState([]);
  const [availablePages, setAvailablePages] = useState(1);
  const [selectedTransaction, setSelectedTransaction]: [
    FedNowTransaction | null,
    CallableFunction,
  ] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery]: [TextQuery, CallableFunction] = useState({ text: undefined });

  useEffect(() => {
    window.scrollTo({ top: 200, left: 0, behavior: "smooth" });
  }, []);

  async function fetchTransactions(page: number = 1, query: TextQuery | null) {
    setIsLoading(true);
    let url = `/v2/admin/transfers/instant/?state=${state}&page=${page}`;
    if (query?.text && query.text.length > 0) {
      url += `&search=${encodeURIComponent(query.text)}`;
    }
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      let errorMessage = "Error fetching transactions. Please contact support.";
      try {
        const errData = await res.json();
        if (typeof errData.message === "string") {
          errorMessage = errData.message;
        } else {
          errorMessage = String(Object.values(errData)[0]) || errorMessage;
        }
      } finally {
        sendNotification({
          type: "negative",
          text: errorMessage,
        });
        setIsLoading(false);
      }
      return;
    }
    const data = await res.json();
    const results = data.results.sort((a: FedNowTransaction, b: FedNowTransaction) => {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
    setTransactions(results);
    setAvailablePages(Math.max(1, Math.ceil(data.count / PAGE_SIZE)));
    setIsLoading(false);

    // An ID was passed into the URL
    if (selectedId && selectedId.length > 0) {
      const lookupRes = await fetch(`/v2/admin/transfers/instant/${selectedId}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!lookupRes.ok) {
        return;
      }
      const txn = await lookupRes.json();
      setSelectedTransaction(txn);
      setDrawerOpen(true);
    }
  }

  async function refreshSelectedTransaction() {
    if (!selectedTransaction) {
      return;
    }
    const lookupRes = await fetch(`/v2/admin/transfers/instant/${selectedTransaction.id}/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!lookupRes.ok) {
      return;
    }
    const data = await lookupRes.json();
    setSelectedTransaction(data);
  }

  async function refreshTransactions(page: number = 1, query: TextQuery | null = null) {
    await fetchTransactions(page, query);
  }

  useEffect(() => {
    refreshTransactions();
  }, []);

  return (
    <>
      <SearchBar
        filterQuery={query}
        setFilterQuery={setQuery}
        setPage={setPage}
        submitCallback={refreshTransactions}
      />
      {transactions.length > 0 || isLoading ? (
        <div>
          <TransactionTableHeader />
          <div className={styles.fednowTable}>
            <LoadingSkeleton isLoading={isLoading} showTitle={false} lines={5}>
              {transactions.map((t: FedNowTransaction) => (
                <TransactionRow
                  txn={t}
                  key={t.payment_identification}
                  clickCallback={() => {
                    setSelectedTransaction(t);
                    setDrawerOpen(true);
                  }}
                />
              ))}
            </LoadingSkeleton>
          </div>
        </div>
      ) : (
        <NoResultsFound heading="No transactions found." caption="">
          <Button
            kind="plain"
            label="Clear search"
            onClick={() => {
              setPage(1);
              refreshTransactions();
            }}
          />
        </NoResultsFound>
      )}
      <div className="margin--top">
        <Pagination
          selectedPage={page}
          onPageChange={(p: number) => {
            setPage(p);
            refreshTransactions(p, query);
          }}
          totalPages={availablePages}
        />
      </div>
      <FedNowTransactionDrawer
        isOpen={drawerOpen}
        setIsOpen={setDrawerOpen}
        transaction={selectedTransaction}
        refresh={() => {
          refreshSelectedTransaction();
          refreshTransactions();
        }}
      />
    </>
  );
}

const TransactionTableHeader = () => (
  <div className="padding--bottom--xs padding--top--s padding--x" style={{ width: "100%" }}>
    <Row alignItems="center">
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>SENDER</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>SOURCE</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>DESTINATION</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>LAST UPDATED</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>ISSUE</div>
      </Row.Item>
      <Row.Item>
        <div className={`fontWeight--bold ${styles.columnHeaderText}`}>STATUS</div>
      </Row.Item>
      <Row.Item shrink>
        <div className={`fontWeight--bold ${styles.columnHeaderText} ${styles.lastRowColumn}`}>
          AMOUNT
        </div>
      </Row.Item>
    </Row>
  </div>
);

const TransactionRow = ({
  txn,
  clickCallback,
}: {
  txn: FedNowTransaction;
  clickCallback: CallableFunction;
}) => {
  const { institution } = useInstitutionContext();

  return (
    <div
      className={`${styles.fednowTableRow} padding--y--s padding--x`}
      style={{ width: "100%" }}
      onClick={() => {
        clickCallback();
      }}
    >
      <Row alignItems="center">
        <Row.Item>
          <p className="whiteSpace--truncate">{`${txn.user.first_name} ${txn.user.last_name}`}</p>
        </Row.Item>
        <Row.Item>
          <div>{txn.source_account.account_number}</div>
          <small>{institution.short_name}</small>
        </Row.Item>
        <Row.Item>
          <div>
            {txn.external_account
              ? txn.external_account.account_number
              : txn.unverified_recipient.ach_destination.account_number}
          </div>
          <small>
            {txn.external_account
              ? txn.external_account.institution_name
              : txn.unverified_recipient.ach_destination.institution_name || "Unknown institution"}
          </small>
        </Row.Item>
        <Row.Item>
          <div>{new Date(txn.updated_at).toLocaleString()}</div>
        </Row.Item>
        <Row.Item>
          <div>
            {getEvaluationDescription({
              evaluations: [],
              alloy_evaluations: txn.alloy_evaluations,
            })}
          </div>
        </Row.Item>
        <Row.Item>
          <div>{txn.state && FEDNOW_TRANSACTION_STATES[txn.state]}</div>
        </Row.Item>
        <Row.Item shrink>
          <div className={styles.lastRowColumn}>
            {moneyFormatter.format(txn.amount_cents / 100)}
          </div>
        </Row.Item>
      </Row>
    </div>
  );
};
